import { useState, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Textarea } from '../atoms/Textarea/Textarea';
import { localToday } from '../livestock/livestockDeceased';
import { createIncident, type Incident } from '../../services/incidentService';
import './IncidentDialogs.css';

export interface ReportIncidentDialogProps {
  aquariumId: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (incident: Incident) => void;
}

/** "Something is wrong" (Reef Guard #1824): opens a manual incident around a day. */
export function ReportIncidentDialog({ aquariumId, isOpen, onClose, onCreated }: ReportIncidentDialogProps) {
  const { t } = useTranslation();
  const [today] = useState(localToday);
  const [date, setDate] = useState(today);
  const [note, setNote] = useState('');
  const [dateError, setDateError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!date || date > today) {
      setDateError(t('incidents.report.dateError'));
      return;
    }
    setSaving(true);
    setSaveError(false);
    try {
      onCreated(await createIncident(aquariumId, { anchor_at: new Date(`${date}T12:00:00`).toISOString(), note: note.trim() || null }));
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={t('incidents.report.title')} size="sm" data-testid="report-incident-dialog">
      <form onSubmit={submit} noValidate>
        <Stack gap="4">
          <Text>{t('incidents.report.intro')}</Text>
          {saveError && <Alert>{t('incidents.report.saveError')}</Alert>}
          <Input
            id="incident-date"
            type="date"
            label={t('incidents.report.date')}
            value={date}
            max={today}
            onChange={(e) => {
              setDate(e.target.value);
              setDateError(null);
            }}
            error={dateError ?? undefined}
            required
            disabled={saving}
          />
          <Textarea
            id="incident-note"
            label={t('incidents.report.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('incidents.report.notePlaceholder')}
            rows={3}
            maxLength={2000}
            disabled={saving}
          />
          <div className="incident-dialog__actions">
            <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
              {t('incidents.cancel')}
            </Button>
            <Button type="submit" isLoading={saving}>
              {t('incidents.report.submit')}
            </Button>
          </div>
        </Stack>
      </form>
    </Dialog>
  );
}
